import React, { useMemo } from 'react';
import { TrendingUp, Target, Award, AlertTriangle, Sparkles } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';

/**
 * CommercialPerformancePanel — Phase 3 bis follow-up (04/06/2026).
 *
 * Displays the player's commercial KPIs derived from
 * `argumentationHistory` : success / neutral / failure counts, success
 * rate, trend (last 10 vs first 10), and a one-line narrative summary.
 *
 * Sits on `/strategie` to close the feedback loop : the player can see
 * how their commercial reading is evolving over time.
 */
export default function CommercialPerformancePanel() {
  const history = useGameStore(s => s.argumentationHistory || EMPTY_ARRAY);

  const stats = useMemo(() => {
    if (history.length === 0) return null;
    const total = history.length;
    const successes = history.filter(h => h.outcome === 'success').length;
    const neutrals  = history.filter(h => h.outcome === 'neutral').length;
    const failures  = history.filter(h => h.outcome === 'failure').length;
    const skipped   = history.filter(h => h.outcome === 'skipped').length;
    const successRate = Math.round((successes / total) * 100);

    // Trend : compare success rate of the last 10 vs the first 10.
    let trend = null;
    if (total >= 20) {
      const first = history.slice(0, 10);
      const last  = history.slice(-10);
      const firstSr = (first.filter(h => h.outcome === 'success').length / 10) * 100;
      const lastSr  = (last.filter(h => h.outcome === 'success').length / 10) * 100;
      trend = Math.round(lastSr - firstSr);
    }

    return { total, successes, neutrals, failures, skipped, successRate, trend };
  }, [history]);

  if (!stats) {
    return (
      <div className="surface-card overflow-hidden" data-testid="commercial-performance-empty">
        <div className="flex items-center justify-between px-4 py-3 border-b border-ink-600 bg-amber-500/5">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-amber-400" />
            <h3 className="font-display text-sm font-bold tracking-tight uppercase text-amber-300">
              Performance commerciale
            </h3>
          </div>
        </div>
        <div className="p-4 text-center">
          <p className="text-xs text-slate-500 italic">Acceptez votre premier contrat pour démarrer le tracking.</p>
        </div>
      </div>
    );
  }

  const tone = stats.successRate >= 50
    ? { color: 'text-emerald-300', narrative: 'Vous lisez bien vos clients. Votre instinct commercial s\'affine.' }
    : stats.successRate >= 30
      ? { color: 'text-amber-300', narrative: 'Votre lecture est correcte, mais peut s\'améliorer. Observez les indices avant de choisir.' }
      : { color: 'text-rose-300', narrative: 'Beaucoup d\'argumentations ratées. Lisez attentivement les tells secteur / loyauté avant de jouer.' };

  return (
    <div className="surface-card overflow-hidden" data-testid="commercial-performance-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-ink-600 bg-amber-500/5">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-amber-400" />
          <h3 className="font-display text-sm font-bold tracking-tight uppercase text-amber-300">
            Performance commerciale
          </h3>
        </div>
        <span className="font-mono text-[10px] text-slate-500 tabular">
          [{stats.total} négociation{stats.total > 1 ? 's' : ''}]
        </span>
      </div>

      <div className="p-4 space-y-3">
        {/* Hero — success rate */}
        <div className="flex items-baseline gap-3 px-3 py-3 rounded-md bg-ink-900/40 border border-ink-700" data-testid="success-rate-hero">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Taux de lecture commerciale</p>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className={`text-3xl font-mono font-black ${tone.color}`}>{stats.successRate}%</span>
              {stats.trend !== null && (
                <span
                  className={`text-[11px] font-mono font-bold ${stats.trend > 0 ? 'text-emerald-300' : stats.trend < 0 ? 'text-rose-300' : 'text-slate-400'}`}
                  data-testid="success-rate-trend"
                  title="Évolution entre vos 10 premières et vos 10 dernières négociations"
                >
                  {stats.trend > 0 ? '+' : ''}{stats.trend} pts vs début
                </span>
              )}
            </div>
          </div>
          <Target className={`w-7 h-7 ${tone.color}`} />
        </div>

        {/* Breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="argumentation-breakdown">
          <KPI icon={Award} label="Succès" value={stats.successes} sub="+5% tarif" color="text-emerald-300" testid="kpi-success" />
          <KPI icon={Sparkles} label="Neutres" value={stats.neutrals} sub="Base" color="text-amber-300" testid="kpi-neutral" />
          <KPI icon={AlertTriangle} label="Échecs" value={stats.failures} sub="−5% remise" color="text-rose-300" testid="kpi-failure" />
          <KPI icon={Sparkles} label="Skip" value={stats.skipped} sub="Commercial" color="text-slate-300" testid="kpi-skipped" />
        </div>

        {/* Narrative */}
        <div
          className={`px-3 py-2 rounded-md border bg-ink-900/30 text-xs italic leading-relaxed ${tone.color.replace('text-', 'border-').replace('-300', '-500/30')}`}
          data-testid="commercial-narrative"
        >
          « {tone.narrative} »
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div className="px-2.5 py-2 rounded-md bg-ink-900/40 border border-ink-700" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-wider font-bold">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`text-xl font-mono font-bold mt-0.5 ${color}`}>{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}
